import React from 'react';
import {
  ActivityIndicator,
  Keyboard,
  Platform,
  Pressable,
  TextInput as RNTextInput,
  View,
  type ViewStyle,
} from 'react-native';

import { avatarDefault, imgMockAcademy } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Image, SvgIcon, Switch, Text, TextInput } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useCurrentLocation, useScaling, type Coords } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

import { useNearbyCoaches } from '@/features/onboarding/queries/use-nearby-coaches';
import { useDebounce } from '@/hooks/use-debounce';
import { KeyboardController } from 'react-native-keyboard-controller';
import { OnboardingAcademyMap } from './onboarding-academy-map';

const strings = STRINGS.onboarding.step3;

// Figma colours not yet in the shared palette.
const META_TEXT = guardianColors['6'];

/** Card left-accent colour per coach category (design: certified = blue, pathway = red).
 *  TODO(confirm): verify the category→colour mapping if more categories are added. */
const CATEGORY_ACCENT: Record<string, string> = {
  'CERTIFIED COACH': guardianColors['Main/Main'],
  'PATHWAY COACH': guardianColors.Error,
};

/**
 * Web-only: promote each coach row onto its own GPU compositing layer.
 *
 * On mobile Safari (and intermittently Chrome) the border-radius descendants in this list —
 * the round avatar <Image> and the radio — leave a stale, vertically-offset "ghost" copy when
 * the list scrolls, so they look doubled. The trigger is the Google map sharing this scroll
 * area: it forces the region onto a composited path that Safari doesn't repaint cleanly.
 * Giving the row its own backing store (translateZ(0) + backface-visibility, kept warm with
 * will-change) makes it repaint as a single unit and kills the ghosting for everything inside
 * it. `Platform.select` keeps this off native, where the properties don't apply.
 *
 * Applied to the inner content wrapper only — never the bordered card — because compositing a
 * border-radius + border element renders the rounded border broken on Chrome/Windows (fractional DPI).
 */
const WEB_STABILIZE_LAYER = (Platform.select({
  web: {
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    willChange: 'transform',
  },
  default: {},
}) ?? {}) as ViewStyle;

/** A coach the guardian can pick from. Coordinates drive the map markers. */
export type NearbyCoach = {
  id: string;
  /** Coach's display name. */
  name: string;
  /** Academy / club the coach is affiliated with. */
  academy: string;
  /** Full venue address, shown in the list only when "Compact view" is toggled on. */
  address?: string;
  /** Whether to show the verified badge next to the name. */
  verified?: boolean;
  /** Coach's venue location — drives both the map marker and the distance to the guardian. */
  coordinate: { latitude: number; longitude: number };
  /** Thumbnail image; falls back to the default user image. */
  thumb?: React.ComponentProps<typeof Image>['source'];
  /** Award logo image URLs (e.g. "50 Masters"), shown as a row under the address. */
  awards?: string[];
  /** Certification badge image URLs (e.g. "Certified Coach"), shown next to the awards. */
  badges?: string[];
  /** Coach category (e.g. "CERTIFIED COACH") — drives the card's left-accent colour. */
  category?: string;
};

/** How far from the guardian we ever look for a coach. Anything past this is dropped
 *  from both the map and the Nearest list, regardless of how far the map is zoomed out. */
const MAX_SEARCH_MILES = 500;

/** Great-circle distance in miles between two coordinates (haversine). */
const distanceInMiles = (
  from: { latitude: number; longitude: number },
  to: { latitude: number; longitude: number },
): number => {
  const R = 3958.8; // Earth's mean radius in miles.
  const rad = Math.PI / 180;
  const dLat = (to.latitude - from.latitude) * rad;
  const dLng = (to.longitude - from.longitude) * rad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(from.latitude * rad) * Math.cos(to.latitude * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

type OnboardingStepFindAcademyProps = {
  coaches?: NearbyCoach[];
  /** Currently selected coach id. Uncontrolled (defaults to the first) when omitted. */
  selectedId?: string | null;
  /**
   * Initial selection when uncontrolled — e.g. the guardian's first favorite coach. Falls
   * back to the first coach in the list. Ignored when `selectedId` is provided.
   */
  initialSelectedId?: string | null;
  onSelect?: (id: string) => void;
  /** Bumped by the parent scroll when it nears the bottom — triggers loading the next page. */
  loadMoreSignal?: number;
};

/**
 * Onboarding step 3 — "Find a nearby academy or coach". A map of nearby academies
 * sits above a search field and a selectable "Nearest" list. The wizard's footer
 * drives "Continue" / "Back".
 */
export function OnboardingStepFindAcademy({
  coaches: coachesProp,
  selectedId,
  initialSelectedId,
  onSelect,
  loadMoreSignal,
}: OnboardingStepFindAcademyProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  // Real coaches fetched from the directory API (populated by the effect below, once the
  // guardian's location is known). A caller-supplied `coaches` prop still wins when present.
  const [remoteCoaches, setRemoteCoaches] = React.useState<NearbyCoach[]>([]);
  const coaches = coachesProp ?? remoteCoaches;

  const [query, setQuery] = React.useState('');
  // "Compact view" toggle above the list: when on, each row also shows its full address.
  const [compactView, setCompactView] = React.useState(true);
  const [internalSelected, setInternalSelected] = React.useState<string | null>(
    initialSelectedId ?? coaches[0]?.id ?? null,
  );
  const isControlled = selectedId !== undefined;
  const selected = isControlled ? selectedId : internalSelected;

  // Once the guardian taps a row we stop letting a late-arriving `initialSelectedId`
  // (e.g. the profile resolving after mount) clobber their choice.
  const userPicked = React.useRef(false);

  // The coach to recentre the map on. A fresh object per selection so re-tapping the same
  // coach (marker or list) still re-triggers the map's focus animation.
  const [focusCoordinate, setFocusCoordinate] = React.useState<Coords | null>(null);

  const handleSelect = React.useCallback(
    (id: string) => {
      userPicked.current = true;
      const coach = coaches.find(c => c.id === id);
      if (coach) setFocusCoordinate({ ...coach.coordinate });
      if (isControlled) onSelect?.(id);
      else setInternalSelected(id);
    },
    [coaches, isControlled, onSelect],
  );

  // Uncontrolled only: seed from `initialSelectedId` when it lands after mount (before any
  // tap), and keep the parent in sync with the resolved selection so "Continue" can persist
  // it even when the guardian never changes it.
  React.useEffect(() => {
    if (isControlled || userPicked.current) return;
    if (initialSelectedId && initialSelectedId !== internalSelected) {
      setInternalSelected(initialSelectedId);
    }
  }, [isControlled, initialSelectedId, internalSelected]);

  React.useEffect(() => {
    if (!isControlled && internalSelected) onSelect?.(internalSelected);
  }, [isControlled, internalSelected, onSelect]);

  // Ask for location once, on mount (not at app launch). `userLocation` is the resolved
  // position; `resolved` flips true once we know the outcome so we only show the "no
  // location" state after a real denial — never a flash before the prompt is answered.
  const {
    request: requestLocation,
    watch: watchLocation,
    geocode: geocodeAddress,
  } = useCurrentLocation();
  const [userLocation, setUserLocation] = React.useState<Awaited<
    ReturnType<typeof requestLocation>
  > | null>(null);
  const [resolved, setResolved] = React.useState(false);

  const inputRef = React.useRef<RNTextInput>(null);

  React.useEffect(() => {
    let cancelled = false;
    let subscription: Awaited<ReturnType<typeof watchLocation>> = null;
    (async () => {
      const coords = await requestLocation();
      if (cancelled) return;
      setUserLocation(coords);
      setResolved(true);
      if (!coords) return;
      // Once located, keep following the guardian as they move (walking/driving). Updates
      // are batched to ~one per 6s in the hook, so the list re-sorts without thrashing.
      subscription = await watchLocation(next => {
        if (!cancelled) setUserLocation(next);
      });
      if (cancelled) subscription?.remove();
    })();
    return () => {
      cancelled = true;
      subscription?.remove();
    };
  }, [requestLocation, watchLocation]);

  // Web has no native "my location" button; the map renders its own and calls this to (re-)fetch
  // the device position on tap (a valid user gesture for the browser geolocation prompt). Native
  // uses `showsMyLocationButton` instead, so this is only wired through on web.
  const handleRequestLocation = React.useCallback(async () => {
    const coords = await requestLocation();
    if (coords) setUserLocation(coords);
    return coords;
  }, [requestLocation]);

  // When the guardian searches an address / zip code in the map bar, we geocode it and use
  // that place as the search centre instead of their GPS position. `null` = follow GPS.
  const [searchedLocation, setSearchedLocation] = React.useState<Coords | null>(null);
  // Drives the spinner in the map's search bar while a geocode is in flight.
  const [searching, setSearching] = React.useState(false);
  // Ignore a slow geocode whose result lands after a newer search has been issued.
  const searchSeq = React.useRef(0);

  const handleSearch = React.useCallback(
    async (text: string) => {
      const seq = ++searchSeq.current;
      // Cleared the field → drop the override and fall back to GPS.
      if (!text) {
        setSearchedLocation(null);
        setSearching(false);
        return;
      }
      setSearching(true);
      const coords = await geocodeAddress(text);
      // A newer search fired while we were resolving — let it own the state.
      if (seq !== searchSeq.current) return;
      setSearching(false);
      // Not found — leave the current centre as-is.
      if (!coords) return;
      setSearchedLocation(coords);
    },
    [geocodeAddress],
  );

  // The centre everything is measured/rendered from: the searched place when set, else GPS.
  const activeLocation = searchedLocation ?? userLocation;

  // Fetch real coaches from the directory around the active centre and feed them into
  // `remoteCoaches` (which drives `coaches` above). Skipped while a caller passes its own list.
  const {
    coaches: fetchedCoaches,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNearbyCoaches(coachesProp ? null : activeLocation, MAX_SEARCH_MILES);
  React.useEffect(() => {
    if (!coachesProp) setRemoteCoaches(fetchedCoaches);
  }, [coachesProp, fetchedCoaches]);

  // Parent scroll nears the bottom → bumps `loadMoreSignal` → load the next page (if any).
  React.useEffect(() => {
    if (!loadMoreSignal || coachesProp) return;
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [loadMoreSignal, coachesProp, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const hasLocation = activeLocation != null;
  // No location to show: grey the map, drop the markers, and swap the list for the
  // "enter your zip code" empty state. Gated on `resolved` so we only grey out once we KNOW
  // there's nothing — never while the first fix is still in flight (which would leave the map
  // stuck grey forever if that fix hangs, as `!resolved` did).
  const noLocation = !hasLocation;

  // The universe of results: coaches within MAX_SEARCH_MILES of the active centre (searched
  // place or GPS), nearest-first. Null until we have a centre to measure against.
  const nearbyCoaches = React.useMemo(() => {
    if (!activeLocation) return null;
    const isPathway = (coach: NearbyCoach) => coach.category === 'PATHWAY COACH';
    return coaches
      .map(coach => ({ coach, miles: distanceInMiles(activeLocation, coach.coordinate) }))
      .filter(({ miles }) => miles <= MAX_SEARCH_MILES)
      .sort((a, b) => {
        // Pathway coaches float to the top; within each group, nearest first.
        const byCategory = Number(isPathway(b.coach)) - Number(isPathway(a.coach));
        return byCategory !== 0 ? byCategory : a.miles - b.miles;
      });
  }, [coaches, activeLocation]);

  // Coaches to plot on the map: the in-range set (clustering handles what's actually on
  // screen). Empty until located, so the greyed-out map stays marker-free.
  const mapCoaches = React.useMemo(
    () => (nearbyCoaches ? nearbyCoaches.map(({ coach }) => coach) : []),
    [nearbyCoaches],
  );

  // The Nearest list: in-range coaches, further narrowed to the visible map region as the
  // guardian zooms/pans and to the search text (matched on coach name, case-insensitive).
  // Before we have a location, fall back to the raw source order with blank distances (the
  // list only renders meaningfully once located).
  const rankedCoaches = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matchesName = (coach: NearbyCoach) =>
      !needle || coach.name.toLowerCase().includes(needle);
    if (!nearbyCoaches) {
      return [];
    }
    return nearbyCoaches
      .filter(({ coach }) => matchesName(coach))
      .map(({ coach, miles }) => {
        const formattedMiles = miles.toFixed(2);
        return {
          coach,
          distance: `${formattedMiles} ${Number(formattedMiles) === 1 ? 'Mile' : 'Miles'}`,
        };
      });
  }, [nearbyCoaches, query]);

  const dismissKeyboard = React.useCallback(() => {
    inputRef.current?.blur();
    Keyboard.dismiss();
    KeyboardController.dismiss();
  }, []);

  const emitDismiss = useDebounce(() => {
    dismissKeyboard();
  }, 1000);

  return (
    <Stack>
      {/* Heading */}
      <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
        {strings.title}
      </Text>
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.subtitle}>
        {strings.subtitle}
      </Text>

      {/* Map (with the address / zip-code search floating over the top) */}
      <OnboardingAcademyMap
        academies={mapCoaches}
        onSelectAcademy={handleSelect}
        userLocation={activeLocation}
        myLocation={userLocation}
        onRequestLocation={handleRequestLocation}
        searchedLocation={searchedLocation}
        onSearch={handleSearch}
        disabled={resolved && noLocation}
        searching={searching}
        focusCoordinate={focusCoordinate}
        selectedId={selected}
      />

      {/* Search */}
      <Stack marginTop={scale(8)}>
        <TextInput
          value={query}
          onChangeText={text => {
            setQuery(text);
            emitDismiss();
          }}
          status="default">
          <TextInput.Content style={styles.searchContent}>
            <TextInput.Icon
              position="left"
              name="IcSearch1"
              size={scale(20)}
              color={guardianColors['6']}
            />
            <TextInput.Field
              ref={inputRef}
              placeholder={strings.searchPlaceholder}
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              autoCapitalize="none"
              maxLength={100}
              autoCorrect={false}
            />
            {query.length > 0 ? (
              <Pressable
                onPress={() => setQuery('')}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel="Clear search"
                style={styles.clearButton}>
                <SvgIcon name="IcXCloseCircleFilled" size={scale(18)} color={guardianColors['6']} />
              </Pressable>
            ) : null}
          </TextInput.Content>
        </TextInput>
      </Stack>

      {/* Nearest list */}
      <Stack direction="row" align="center" justify="space-between" style={styles.nearestHeader}>
        <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.nearest}>
          {strings.nearest}
        </Text>
        {/* Only offer the toggle once there's a populated list to expand. */}
        {!noLocation && rankedCoaches.length > 0 ? (
          <CompactViewToggle value={compactView} onValueChange={setCompactView} />
        ) : null}
      </Stack>
      {noLocation ? (
        <NoLocationState />
      ) : hasLocation && rankedCoaches.length === 0 ? (
        // We have a centre (GPS or searched) but nothing matches the radius / visible area
        // / name filter.
        <NotFoundState />
      ) : (
        <Stack gap={scale(12)}>
          {rankedCoaches.map(({ coach, distance }) => (
            <CoachCard
              key={coach.id}
              coach={coach}
              distance={distance}
              expanded={!compactView}
              selected={coach.id === selected}
              onPress={() => handleSelect(coach.id)}
            />
          ))}
          {isFetchingNextPage ? (
            <Stack align="center" paddingY={scale(8)}>
              <ActivityIndicator color={guardianColors['Main/Main']} />
            </Stack>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}

/** Empty state shown in place of the Nearest list when location permission was denied or
 *  is unavailable: a struck-through location glyph and a nudge to search by zip code. */
function NoLocationState() {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack align="center" gap={scale(8)} style={styles.noLocation}>
      <View style={styles.noLocationIcon}>
        <SvgIcon name="IcGpsSlash" size={scale(24)} color={guardianColors['6']} />
      </View>
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.noLocationTitle}>
        {strings.noLocationTitle}
      </Text>
      <Text
        variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
        style={styles.noLocationSubtitle}>
        {strings.noLocationSubtitle}
      </Text>
    </Stack>
  );
}

/** Empty state shown when we have a location but no coach matches the current search
 *  criteria (radius / visible area / name). Same layout as {@link NoLocationState}. */
function NotFoundState() {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack align="center" gap={scale(8)} style={styles.noLocation}>
      <View style={styles.noLocationIcon}>
        <SvgIcon name="IcLocationSlash" size={scale(24)} color={guardianColors['6']} />
      </View>
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.noLocationTitle}>
        {strings.notFoundTitle}
      </Text>
      <Text
        variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
        style={styles.noLocationSubtitle}>
        {strings.notFoundSubtitle}
      </Text>
    </Stack>
  );
}

/** "Compact view" label + switch shown at the right of the Nearest header. Toggling it on
 *  expands each list row with its full address. Custom switch (no design-system Switch). */
function CompactViewToggle({
  value,
  onValueChange,
}: {
  value: boolean;
  onValueChange: (next: boolean) => void;
}) {
  const styles = useStyles();
  return (
    <View style={styles.compactToggle}>
      <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={styles.compactLabel}>
        {strings.compactView}
      </Text>
      <Switch value={value} onValueChange={onValueChange} />
    </View>
  );
}

type CoachCardProps = {
  coach: NearbyCoach;
  /** Pre-formatted distance from the guardian, e.g. "0.73 Miles". Omitted until located. */
  distance?: string;
  /** Expanded (full) row: shows the address + award/badge logos. Compact hides both. */
  expanded?: boolean;
  selected: boolean;
  onPress: () => void;
};

/** A selectable coach row: avatar, name + verified badge, academy, (address + logos when
 *  expanded), distance, and a corner check badge when selected. */
function CoachCard({ coach, distance, expanded, selected, onPress }: CoachCardProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  const accentColor = coach.category ? CATEGORY_ACCENT[coach.category] : undefined;
  // Selection colour follows the coach's category (pathway = red, certified = navy),
  // falling back to navy for coaches without a category. Drives both the selected
  // border and the corner check badge.
  const indicatorColor = accentColor ?? guardianColors['Main/Main'];
  const logos = [...(coach.awards ?? []), ...(coach.badges ?? [])];

  return (
    <Pressable
      onPress={onPress}
      style={[
        styles.card,
        accentColor ? styles.cardAccent : null,
        accentColor ? { borderLeftColor: accentColor } : null,
        selected ? { borderColor: indicatorColor } : null,
      ]}
      accessibilityRole="radio"
      accessibilityState={{ selected }}>
      <Stack direction="row" align="flex-start" gap={scale(10)} style={styles.cardInner}>
        <Image
          source={coach.thumb ?? imgMockAcademy}
          defaultSource={avatarDefault}
          style={styles.thumb}
          resizeMode="cover"
        />

        <Stack flex={1} gap={scale(4)}>
          <Stack direction="row" align="center" gap={scale(4)}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
              style={styles.coachName}
              numberOfLines={1}>
              {coach.name}
            </Text>
            {coach.verified ? (
              <SvgIcon
                name="IcCheckmarkBadge"
                size={scale(20)}
                color={accentColor ?? guardianColors['Main/Main']}
              />
            ) : null}
          </Stack>

          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
            style={styles.academyName}
            numberOfLines={2}>
            {coach.academy}
          </Text>

          {expanded && coach.address ? (
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
              style={styles.address}
              numberOfLines={2}>
              {coach.address}
            </Text>
          ) : null}

          {distance ? (
            <Stack direction="row" align="center" gap={scale(4)}>
              <SvgIcon name="IcDirect" size={scale(12)} color={guardianColors['Main/Main']} />
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 10 Regular"
                style={styles.distance}>
                {distance}
              </Text>
            </Stack>
          ) : null}

          {expanded && logos.length > 0 ? (
            <Stack direction="row" align="center" gap={scale(12)} style={styles.badgeRow}>
              {logos.map((url, index) => (
                <Image
                  key={`${url}-${index}`}
                  source={{ uri: url }}
                  style={styles.badgeLogo}
                  resizeMode="contain"
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Stack>

      {/* Selection indicator: a check badge tucked into the top-right corner. */}
      {selected ? (
        <View style={[styles.selectBadge, { backgroundColor: indicatorColor }]}>
          <SvgIcon name="IcCheckMd" size={scale(14)} color={colors['0']} strokeWidth={1.5} />
        </View>
      ) : null}
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  title: {
    color: colors['10'],
  },
  subtitle: {
    marginTop: scale(8),
    color: colors['10'],
  },
  searchContent: {
    height: scale(48),
    paddingLeft: scale(12),
    paddingRight: scale(12),
    borderRadius: scale(8),
    borderColor: colors['3'],
    backgroundColor: colors['0'],
  },
  clearButton: {
    marginLeft: scale(4),
  },
  nearestHeader: {
    marginTop: scale(16),
    marginBottom: scale(8),
  },
  nearest: {
    color: colors['10'],
  },
  compactToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
  },
  compactLabel: {
    color: colors['10'],
  },
  noLocation: {
    paddingVertical: scale(10),
    paddingHorizontal: scale(12),
  },
  noLocationIcon: {
    width: scale(48),
    height: scale(48),
    borderRadius: scale(24),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors['0'],
    marginBottom: scale(4),
  },
  noLocationTitle: {
    color: guardianColors['6'],
    textAlign: 'center',
  },
  noLocationSubtitle: {
    color: META_TEXT,
    textAlign: 'center',
  },
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderWidth: scale(1),
    borderColor: 'transparent',
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
    overflow: 'hidden',
  },
  // The GPU-layer hack lives on the inner content, NOT the bordered card: on Chrome/Windows
  // (fractional DPI) compositing an element that has border-radius + border renders the
  // rounded border broken. Keeping it off the card fixes that while still stabilising the
  // round avatar inside against Safari scroll-ghosting.
  cardInner: {
    ...WEB_STABILIZE_LAYER,
  },
  cardAccent: {
    borderLeftWidth: scale(4),
  },
  thumb: {
    width: scale(50),
    height: scale(50),
    borderRadius: scale(25),
    borderWidth: scale(1),
    borderColor: colors['1'],
    backgroundColor: colors['1'],
  },
  coachName: {
    flexShrink: 1,
    color: colors['10'],
  },
  academyName: {
    color: colors['10'],
  },
  address: {
    color: META_TEXT,
  },
  distance: {
    color: guardianColors['Main/Main'],
  },
  badgeRow: {
    flexWrap: 'wrap',
  },
  badgeLogo: {
    height: scale(40),
    width: scale(40),
  },
  selectBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: scale(20),
    height: scale(20),
    backgroundColor: guardianColors['Main/Main'],
    // Quarter-circle: top-right hugs the card corner, bottom-left is a large
    // concave sweep. Check sits toward the top-right (padding biases it there).
    borderTopRightRadius: scale(11),
    borderBottomLeftRadius: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: scale(3),
    paddingBottom: scale(3),
  },
}));

export default OnboardingStepFindAcademy;
